import { useEffect, useState } from "react";
import { openWithCacheBuster } from "./cacheBuster";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Copy, ExternalLink, FileText } from "lucide-react";
import { CharacterCountBar } from "./CharacterCountBar";
import type {
  DuplicateMetaDetails,
  DuplicateMetaFix,
  PageIssue,
  ProposedFix,
} from "@/types/technicalAudit";

const META_LIMITS = { min: 120, max: 160, optimal: { min: 150, max: 160 } };

interface DuplicateMetaComparisonProps {
  issue: PageIssue;
  onEdit: (value: ProposedFix) => void;
}

function distinguishingSlug(url: string): string {
  try {
    const path = new URL(url).pathname.replace(/\/+$/, "");
    const seg = path.split("/").filter(Boolean).pop();
    return seg ? decodeURIComponent(seg) : "home";
  } catch {
    return url;
  }
}

function pathOf(url: string): string {
  try {
    return new URL(url).pathname || url;
  } catch {
    return url;
  }
}

function normalizedUrlKey(url: string): string {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
    const path = parsed.pathname.replace(/\/+$/, "") || "/";
    return `${host}${path}`;
  } catch {
    return url
      .replace(/^https?:\/\/(www\.)?/i, "")
      .replace(/\/+$/, "")
      .toLowerCase();
  }
}

function isSameUrl(a: string, b: string): boolean {
  return normalizedUrlKey(a) === normalizedUrlKey(b);
}

export function DuplicateMetaComparison({
  issue,
  onEdit,
}: DuplicateMetaComparisonProps) {
  const details = (issue.issue_details || {}) as DuplicateMetaDetails;
  const rawDetails = details as DuplicateMetaDetails & Record<string, unknown>;
  const sharedDescription =
    details.description ||
    (rawDetails.duplicate_meta_description as string) ||
    "";
  const urls = details.duplicate_urls || [];
  const currentUrl = issue.page_url;
  const urlsWithCurrent = urls.some((url) => isSameUrl(url, currentUrl))
    ? urls
    : [currentUrl, ...urls];
  const groupedUrls = [
    ...urlsWithCurrent.filter((url) => isSameUrl(url, currentUrl)),
    ...urlsWithCurrent.filter((url) => !isSameUrl(url, currentUrl)),
  ];
  const orderedUrls = groupedUrls.length ? groupedUrls : [currentUrl];
  const hasKnownSiblings = orderedUrls.some(
    (url) => !isSameUrl(url, currentUrl),
  );
  const displayedPageCount = orderedUrls.length;
  const pageNoun = displayedPageCount === 1 ? "page" : "pages";

  const proposed = issue.proposed_fix as
    | (DuplicateMetaFix & { reasoning?: string; new_meta_description?: string })
    | null;
  const approved = issue.approved_value as
    | (DuplicateMetaFix & { reasoning?: string; new_meta_description?: string })
    | null;
  const draftKey = `${issue.id}:${issue.issue_type}:${JSON.stringify(issue.approved_value ?? issue.proposed_fix ?? "")}`;
  const initialMeta =
    approved?.new_meta_description || proposed?.new_meta_description || "";
  const [draft, setDraft] = useState<{ key: string; meta: string } | null>(
    null,
  );
  const editedMeta = draft?.key === draftKey ? draft.meta : initialMeta;
  const reasoning = approved?.reasoning || proposed?.reasoning;

  useEffect(() => {
    const value = {
      new_meta_description: editedMeta.trim() || undefined,
    };
    onEdit(value as ProposedFix);
  }, [editedMeta, onEdit]);

  const hasProposed = Boolean(
    approved?.new_meta_description || proposed?.new_meta_description,
  );
  return (
    <div className="space-y-4">
      <Card className="border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center">
              <Copy className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            {hasKnownSiblings
              ? `Same meta description on ${displayedPageCount} ${pageNoun}`
              : "Duplicate meta description detected"}
            <Badge
              variant="outline"
              className={
                hasKnownSiblings
                  ? "ml-auto bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800"
                  : "ml-auto bg-muted text-foreground border-border"
              }
            >
              {hasKnownSiblings ? "Conflict" : "Review"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <blockquote className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-4 rounded-r-lg">
            <p className="text-sm text-amber-900 dark:text-amber-300 italic">
              &ldquo;{sharedDescription}&rdquo;
            </p>
          </blockquote>
          <CharacterCountBar
            current={sharedDescription.length}
            min={META_LIMITS.min}
            max={META_LIMITS.max}
            optimal={META_LIMITS.optimal}
          />
        </CardContent>
      </Card>

      {!hasKnownSiblings && (
        <p className="text-xs text-muted-foreground">
          This issue did not include sibling URLs. Write a unique meta
          description for this page, then use a follow-up crawl to confirm the
          duplicate group is resolved.
        </p>
      )}

      <div
        className={
          hasKnownSiblings
            ? "grid grid-cols-1 gap-3 lg:grid-cols-2"
            : "grid grid-cols-1 gap-3"
        }
      >
        {orderedUrls.map((url) => {
          const isCurrent = isSameUrl(url, currentUrl);
          return (
            <Card
              key={url}
              className={
                isCurrent
                  ? "border-teal-dark/40 bg-teal-light/40 shadow-sm"
                  : "border-border shadow-sm"
              }
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-xs font-semibold">
                  {isCurrent ? (
                    <Badge className="bg-teal text-white hover:bg-teal-hover">
                      This page
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-muted text-muted-foreground border-border"
                    >
                      Sibling
                    </Badge>
                  )}
                  <span className="font-mono text-foreground truncate">
                    /{distinguishingSlug(url)}
                  </span>
                  <a
                    href={url}
                    onClick={(e) => openWithCacheBuster(e, url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-muted-foreground hover:text-muted-foreground"
                    title="Open page"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <p className="text-[11px] font-mono break-all text-muted-foreground">
                  {pathOf(url)}
                </p>

                <div>
                  <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Current meta description
                  </div>
                  <div className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground">
                    {sharedDescription}
                  </div>
                </div>

                {isCurrent ? (
                  <div>
                    <div className="mb-1 flex items-center gap-1 text-xs font-medium text-teal-dark">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      New meta description (editable)
                    </div>
                    {hasProposed || editedMeta ? (
                      <>
                        <Textarea
                          value={editedMeta}
                          onChange={(event) =>
                            setDraft({
                              key: draftKey,
                              meta: event.target.value,
                            })
                          }
                          className="min-h-[96px] text-sm"
                          placeholder="Write a unique meta description for this page..."
                        />
                        <div className="mt-2">
                          <CharacterCountBar
                            current={editedMeta.trim().length}
                            min={META_LIMITS.min}
                            max={META_LIMITS.max}
                            optimal={META_LIMITS.optimal}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="rounded-md border border-dashed bg-card p-3 text-xs text-muted-foreground">
                        Click <span className="font-medium">Generate fix</span>{" "}
                        above to draft a unique meta description, then edit it
                        here.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40/40 p-2 text-[11px] text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                    Still shares the duplicate meta description - resolve this
                    page from its own audit row.
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {reasoning && (
        <div className="rounded-md bg-blue-50 dark:bg-blue-950/40 p-3">
          <p className="text-xs font-medium text-blue-800 dark:text-blue-300">AI Reasoning:</p>
          <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">{reasoning}</p>
        </div>
      )}
    </div>
  );
}
